import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { ArrowLeft, Plus, Loader2, AlertTriangle } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { API_BASE } from '@/lib/api';
import { Button } from '@/components/ui/button';

const CATEGORIES = ['WEEKLY', 'WEEKEND', 'DAILY', 'TOURNAMENT', 'SPECIAL'];
const FORMATS = ['CLASSIC', 'SHOWDOWN', 'FREE_ROLL', 'SALARY_CAP', 'BEST_BALL', 'DRAFT_PPR'];
const TIERS = ['FREE', 'STANDARD', 'PREMIUM', 'ELITE', 'CHAMPIONSHIP'];
const STATUSES = ['UPCOMING', 'OPEN', 'LOCKED', 'LIVE', 'SCORING', 'COMPLETED', 'CANCELLED'];
const ENTRY_FEE_TYPES = ['FREE', 'COINS', 'GEMS', 'USD'];
const PRIZE_TYPES = ['COINS', 'GEMS', 'USD', 'CARDS', 'MIXED'];
const RARITY_GATES = ['OPEN', 'CORE', 'RARE', 'ELITE', 'LEGENDARY', 'PRIME'];
const CARD_INTEGRATIONS = ['NONE', 'OPTIONAL', 'REQUIRED', 'CARDS_ONLY'];

// Get default dates (start: next Monday, end: next Sunday)
const getDefaultDates = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const daysUntilMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek;

  const startDate = new Date(now);
  startDate.setDate(now.getDate() + daysUntilMonday);
  startDate.setHours(19, 0, 0, 0); // 7pm

  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6);
  endDate.setHours(23, 59, 0, 0);

  return {
    startTime: startDate.toISOString().slice(0, 16),
    endTime: endDate.toISOString().slice(0, 16),
  };
};

export default function AdminEventNewPage() {
  const router = useRouter();
  const { accessToken } = useAuthStore();
  const defaultDates = getDefaultDates();

  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  // Form state with sensible defaults
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    imageUrl: '',
    sport: 'NBA',
    category: 'WEEKLY',
    format: 'CLASSIC',
    tier: 'STANDARD',
    status: 'UPCOMING',
    season: 2025,
    week: '',
    entryFee: 100,
    entryFeeType: 'COINS',
    maxEntries: '',
    prizePool: 10000,
    prizeType: 'COINS',
    entryOpenTime: '',
    entryDeadline: '',
    startTime: defaultDates.startTime,
    endTime: defaultDates.endTime,
    rarityGate: 'OPEN',
    requiresCards: true,
    maxCardsPerEntry: 8,
    cardIntegration: 'REQUIRED',
    minCollectorScore: 0,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData((prev) => ({ ...prev, [name]: value === '' ? '' : Number(value) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessToken) return;

    setIsCreating(true);
    setError('');

    try {
      const payload = {
        ...formData,
        week: formData.week ? Number(formData.week) : null,
        maxEntries: formData.maxEntries ? Number(formData.maxEntries) : null,
        entryOpenTime: formData.entryOpenTime || null,
        entryDeadline: formData.entryDeadline || null,
      };

      const res = await fetch(`${API_BASE}/api/admin/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok) {
        router.push(`/admin/events/${data.event.id}`);
      } else {
        setError(data.error || 'Failed to create event');
      }
    } catch (err) {
      setError('Failed to create event');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link
          href="/admin/events"
          className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-white">Create Event</h1>
          <p className="text-sm text-zinc-400">Add a new fantasy event</p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-red-400" />
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-zinc-400 mb-1">Event Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="e.g., NBA Weekly Classic - Week 15"
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-red-500 focus:outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-zinc-400 mb-1">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                placeholder="Describe this event..."
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-red-500 focus:outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-zinc-400 mb-1">Image URL</label>
              <input
                type="url"
                name="imageUrl"
                value={formData.imageUrl}
                onChange={handleChange}
                placeholder="https://..."
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-red-500 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Classification */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Classification</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Category *</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Format *</label>
              <select
                name="format"
                value={formData.format}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none"
              >
                {FORMATS.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Tier</label>
              <select
                name="tier"
                value={formData.tier}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none"
              >
                {TIERS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none"
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Season</label>
              <input
                type="number"
                name="season"
                value={formData.season}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Week (optional)</label>
              <input
                type="number"
                name="week"
                value={formData.week}
                onChange={handleChange}
                placeholder="e.g., 15"
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-red-500 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Entry & Prizes */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Entry & Prizes</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Entry Fee</label>
              <input
                type="number"
                name="entryFee"
                value={formData.entryFee}
                onChange={handleChange}
                min={0}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Entry Fee Type</label>
              <select
                name="entryFeeType"
                value={formData.entryFeeType}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none"
              >
                {ENTRY_FEE_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Max Entries (optional)</label>
              <input
                type="number"
                name="maxEntries"
                value={formData.maxEntries}
                onChange={handleChange}
                min={0}
                placeholder="Unlimited"
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-red-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Prize Pool</label>
              <input
                type="number"
                name="prizePool"
                value={formData.prizePool}
                onChange={handleChange}
                min={0}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Prize Type</label>
              <select
                name="prizeType"
                value={formData.prizeType}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none"
              >
                {PRIZE_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Schedule */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Schedule</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Entry Open Time</label>
              <input
                type="datetime-local"
                name="entryOpenTime"
                value={formData.entryOpenTime}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Entry Deadline</label>
              <input
                type="datetime-local"
                name="entryDeadline"
                value={formData.entryDeadline}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Start Time *</label>
              <input
                type="datetime-local"
                name="startTime"
                value={formData.startTime}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">End Time *</label>
              <input
                type="datetime-local"
                name="endTime"
                value={formData.endTime}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Card Requirements */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Card Requirements</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Rarity Gate</label>
              <select
                name="rarityGate"
                value={formData.rarityGate}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none"
              >
                {RARITY_GATES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Card Integration</label>
              <select
                name="cardIntegration"
                value={formData.cardIntegration}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none"
              >
                {CARD_INTEGRATIONS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Max Cards Per Entry</label>
              <input
                type="number"
                name="maxCardsPerEntry"
                value={formData.maxCardsPerEntry}
                onChange={handleChange}
                min={1}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-1">Min Collector Score</label>
              <input
                type="number"
                name="minCollectorScore"
                value={formData.minCollectorScore}
                onChange={handleChange}
                min={0}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white focus:border-red-500 focus:outline-none"
              />
            </div>
            <div className="flex items-center">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="requiresCards"
                  checked={formData.requiresCards}
                  onChange={handleChange}
                  className="w-4 h-4 rounded border-zinc-700 bg-zinc-800 text-red-500 focus:ring-red-500"
                />
                <span className="text-sm text-zinc-300">Requires Cards</span>
              </label>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-between">
          <Link
            href="/admin/events"
            className="text-zinc-400 hover:text-white transition-colors"
          >
            Cancel
          </Link>
          <Button type="submit" disabled={isCreating}>
            {isCreating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4" />
                Create Event
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
